import _ from 'lodash';
import React, { useContext, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import ModalComponent from 'common/components/ModalComponent';
import { onReStartRun } from 'common/api/run';
import { runPropTypes } from 'common/propTypes';
import { quotaVerificationDom } from 'ui/QuotaVerification/quotaDom';
import { reactToastify, TOASTIFY_TYPES } from 'common/utils/reactToastify';
import { MARKET_END_TIME } from 'common/constants/index';
import { OrderContext } from '../../OrderStore/index';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';
import { getDeploymentCostMessage } from 'v3/common/components/Algoverse/DeploymentCostAlert';
import AlgoverseDeploymentCostModal from 'v3/common/components/Algoverse/DeploymentCostModal';
import useAlgoverseDeployment from 'v3/common/components/Algoverse/DeploymentCostModal/hook/useAlgoverseDeployment';

const propTypes = {
  instrumentGroups: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  run: runPropTypes.isRequired,
  isLiveRun: PropTypes.bool.isRequired,
  isRunBelongsToUser: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool
};

const defaultProps = { instrumentGroups: [], isDisabled: false };

const ReStartButton = ({ instrumentGroups, run, isLiveRun, isRunBelongsToUser, isDisabled }) => {
  const isValidToRestart = _.get(run, 'is_valid_to_restart', false);

  const [isShowModal, setIsShowModal] = useState(false);
  const [isSkipLastTransaction, setIsSkipLastTransaction] = useState('no');
  const { openDeployFlow, isConfirmationOpen, closeModal, proceedToDeploy } = useAlgoverseDeployment();
  const {
    id: runId,
    order_type: orderType,
    run_type: runType,
    is_strategy_subscriber: isMarketPlaceSubscribedRun,
  } = run;

  const { transactions, isPremiumOrProFeaturesEnabled } = useContext(OrderContext);
  const lastTransaction = _.last(transactions) || {};
  const pairedOrders = _.get(lastTransaction, 'pairedOrders', []);

  const subscriberStrategyCutOffTime = _.get(window, 'SA_COLLECTIONS.subscriberStrategyCutOffTime', null);

  const isLastTransactionNotCompleted = _.some(pairedOrders, (pairedOrder) => {
    const { isManualExitShow } = pairedOrder;
    return isManualExitShow;
  });

  if (!isLiveRun || !isRunBelongsToUser || !isValidToRestart) return null;

  const renderRadioBtn = (optionType) => {
    return (
      <div className="form-check form-check-sm mr-1" key={optionType}>
        <input
          className="form-check-input"
          type="radio"
          id={optionType}
          value={optionType}
          data-track-category="Last Transaction Skip"
          data-track-action={optionType}
          data-track-label="Last Transaction Skip"
          checked={optionType === isSkipLastTransaction}
          onChange={() => {
            setIsSkipLastTransaction(optionType);
          }}
        />
        <label className="form-check-label" htmlFor={`${optionType}`}>
          {_.startCase(optionType)}
        </label>
      </div>
    );
  };

  const executeRestartRun = () => {
    if (isLastTransactionNotCompleted && !isMarketPlaceSubscribedRun) {
      let content = 'Your last transaction is not yet completed ';
      content += 'We ignored those orders, so you have to take care of your orders manually';
      reactToastify(content, TOASTIFY_TYPES.INFO);
    }

    onReStartRun({ ignore_incomplete_transaction: isSkipLastTransaction === 'no' }, runId);
  };

  const onRestart = () => {
    setIsShowModal(false);

    const cutoffTimeMoment = moment(subscriberStrategyCutOffTime, 'HH:mm');
    const marketEndTimeMoment = moment(MARKET_END_TIME, 'HH:mm');

    const isAfterCutoff = moment().isAfter(cutoffTimeMoment);
    const isBeforeMarketEnd = moment().isBefore(marketEndTimeMoment);

    quotaVerificationDom(instrumentGroups, { order_type: orderType, run_type: runType }, (param) => {
    executeRestartRun(param);
    if (isAfterCutoff && isBeforeMarketEnd && isMarketPlaceSubscribedRun) {
      reactToastify(
        'Your deployment will take effect in the live market from tomorrow.',
        TOASTIFY_TYPES.INFO
      );
    }
    });
  };

  const renderModal = () => {
    return (
      <ModalComponent
        onSave={() => {
          onRestart();
        }}
        onClose={() => setIsShowModal(false)}
        title="Restart Confirmation"
        size="sm"
        okBtnTitle="Restart"
      >
        <>
          Your last transaction is not yet completed. Do you want to continue with the last transaction?
          <div className="d-flex align-items-center mt-2">{_.map(['yes', 'no'], renderRadioBtn)}</div>
        </>
      </ModalComponent>
    );
  };

  const tooltipProps = ApplicationDomainUtility.isAlgoverse ? {
    'type': 'button',
    'data-toggle': 'tooltip',
    'data-bs-placement': 'left',
    title: getDeploymentCostMessage(isPremiumOrProFeaturesEnabled),
  } : {};

  return (
    <>
      <button
        className="text-nowrap btn btn-sm btn-outline-success btn-pill common-icon-btn track"
        onClick={() => openDeployFlow(onRestart)}
        disabled={isDisabled}
        key="stop-btn"
        data-track-category="Quant Restart"
        data-track-action="restart"
        data-track-label="Quant Restart"
        {...tooltipProps}
      >
        <i className="material-icons-outlined tx-18 me-2">restart_alt</i>
        Restart
      </button>
      {isShowModal && renderModal()}
      {isConfirmationOpen && (
        <AlgoverseDeploymentCostModal
          isPremium={isPremiumOrProFeaturesEnabled}
          isOpen={isConfirmationOpen}
          onClose={closeModal}
          onSave={() => proceedToDeploy(onRestart)}
        />
      )}
    </>
  );
};

ReStartButton.propTypes = propTypes;
ReStartButton.defaultProps = defaultProps;

export default ReStartButton;
